import { DEBUG_CONFIG } from '@/config/debug';

/**
 * Système de logging conditionnel pour la production
 * Remplace les console.log/error dispersés dans le code
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

interface LogContext {
  component?: string;
  function?: string;
  userId?: string;
  campaignId?: string;
  referentId?: string;
  [key: string]: any;
}

class Logger {
  private level: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';

    // Utiliser la configuration pour déterminer le niveau de log
    const configLevel = this.isDevelopment
      ? DEBUG_CONFIG.DEVELOPMENT_LOG_LEVEL
      : DEBUG_CONFIG.PRODUCTION_LOG_LEVEL;

    this.level = this.getLogLevelFromString(configLevel);
  }

  private getLogLevelFromString(levelString: string): LogLevel {
    switch (levelString.toUpperCase()) {
      case 'DEBUG': return LogLevel.DEBUG;
      case 'INFO': return LogLevel.INFO;
      case 'WARN': return LogLevel.WARN;
      case 'ERROR': return LogLevel.ERROR;
      case 'NONE': return LogLevel.NONE;
      default: return LogLevel.WARN;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.level;
  }

  private formatMessage(level: string, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` [${JSON.stringify(context)}]` : '';
    return `[${timestamp}] ${level}: ${message}${contextStr}`;
  }

  private sanitizeContext(context?: LogContext): LogContext | undefined {
    if (!context || !DEBUG_CONFIG.ENABLE_DATA_SANITIZATION) return context;

    // Supprimer les données sensibles des logs
    const sanitized = { ...context };

    // Masquer les emails partiellement
    if (sanitized.email) {
      const email = sanitized.email as string;
      const [local, domain] = email.split('@');
      sanitized.email = `${local.substring(0, 2)}***@${domain}`;
    }

    // Masquer les IPs partiellement
    if (sanitized.ipAddress) {
      const ip = sanitized.ipAddress as string;
      const parts = ip.split('.');
      if (parts.length === 4) {
        sanitized.ipAddress = `${parts[0]}.${parts[1]}.***.***.`;
      }
    }

    // Supprimer les tokens/clés sensibles
    const sensitiveKeys = ['token', 'apiKey', 'password', 'secret', 'key', 'auth'];
    sensitiveKeys.forEach(key => {
      if (sanitized[key]) {
        delete sanitized[key];
      }
    });

    return sanitized;
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const sanitizedContext = this.sanitizeContext(context);
      console.log(this.formatMessage('DEBUG', message, sanitizedContext));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const sanitizedContext = this.sanitizeContext(context);
      console.info(this.formatMessage('INFO', message, sanitizedContext));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.WARN)) {
      const sanitizedContext = this.sanitizeContext(context);
      console.warn(this.formatMessage('WARN', message, sanitizedContext));
    }
  }

  error(message: string, error?: Error, context?: LogContext): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const sanitizedContext = this.sanitizeContext(context);
      const errorInfo = error ? { error: error.message, stack: error.stack } : {};
      console.error(this.formatMessage('ERROR', message, { ...sanitizedContext, ...errorInfo }));
    }
  }

  // Méthodes spécialisées pour les cas d'usage fréquents
  networkRequest(operation: string, method: string, context?: LogContext): void {
    this.debug(`🌐 ${operation} - ${method} request`, context);
  }

  networkResponse(operation: string, context?: LogContext, duration?: number): void {
    const durationStr = duration ? ` (${duration}ms)` : '';
    this.debug(`✅ ${operation} - Response received${durationStr}`, context);
  }

  networkError(operation: string, error: Error, context?: LogContext): void {
    this.error(`❌ ${operation} - Network error`, error, context);
  }

  contextValidationStart(referentId: string, attempt: number, maxAttempts: number): LogContext {
    const context = { referentId, attempt, maxAttempts };
    this.debug(`🔍 Validation contexte référent - Tentative ${attempt}/${maxAttempts}`, context);
    return context;
  }

  contextValidationSuccess(context: LogContext): void {
    this.info('✅ Validation contexte référent réussie', context);
  }

  contextValidationError(error: Error, context: LogContext): void {
    this.error('❌ Échec validation contexte référent', error, context);
  }

  cacheHit(key: string): void {
    this.debug(`🎯 Cache HIT: ${key}`);
  }

  cacheMiss(key: string): void {
    this.debug(`🔍 Cache MISS: ${key}`);
  }

  cacheSet(key: string, ttl?: number): void {
    const ttlStr = ttl ? ` (TTL: ${ttl}ms)` : '';
    this.debug(`💾 Cache SET: ${key}${ttlStr}`);
  }

  cacheInvalidate(key: string): void {
    this.debug(`🗑️ Cache INVALIDATE: ${key}`);
  }
}

// Instance singleton
export const logger = new Logger();

// Export des types pour utilisation dans d'autres fichiers
export type { LogContext };